/**
 * Popover — Radix `Popover` themed as a paper card with a hairline border.
 * Sharp corners, no shadow halo (one drop shadow), padding owned by the
 * consumer so the popover can host a list, a form, or a callout block. Pairs
 * with `Toolband` action chips and inspector quick-actions.
 *
 * Extrapolated within the design language — no dedicated artboard.
 */

import * as RadixPopover from '@radix-ui/react-popover';
import type { ReactNode } from 'react';

interface PopoverProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Default is uncontrolled; pass `defaultOpen` for an initially open popover. */
  defaultOpen?: boolean;
  children: ReactNode;
}

export function Popover({ open, onOpenChange, defaultOpen, children }: PopoverProps) {
  return (
    <RadixPopover.Root
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
    >
      {children}
    </RadixPopover.Root>
  );
}

interface PopoverTriggerProps {
  children: ReactNode;
  asChild?: boolean;
  className?: string;
}

function Trigger({ children, asChild = true, className = '' }: PopoverTriggerProps) {
  return (
    <RadixPopover.Trigger asChild={asChild} className={className}>
      {children}
    </RadixPopover.Trigger>
  );
}
Popover.Trigger = Trigger;

interface PopoverContentProps {
  children: ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  sideOffset?: number;
  /** Min width — default 240 px to match toolband chip widths. */
  minWidth?: number;
  /** Cap the width; otherwise content sets size. */
  maxWidth?: number;
  className?: string;
  /** Show the small arrow tip — defaults to off; popovers usually omit it. */
  arrow?: boolean;
}

function Content({
  children,
  side = 'bottom',
  align = 'start',
  sideOffset = 8,
  minWidth = 240,
  maxWidth,
  className = '',
  arrow = false,
}: PopoverContentProps) {
  return (
    <RadixPopover.Portal>
      <RadixPopover.Content
        side={side}
        align={align}
        sideOffset={sideOffset}
        collisionPadding={12}
        className={
          'z-50 max-h-[var(--radix-popover-content-available-height)] overflow-y-auto border border-line bg-paper text-ink shadow-[0_12px_32px_rgba(0,0,0,0.18)] focus:outline-none ' +
          'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 ' +
          'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 ' +
          'data-[side=top]:slide-in-from-bottom-1 data-[side=bottom]:slide-in-from-top-1 ' +
          'data-[side=left]:slide-in-from-right-1 data-[side=right]:slide-in-from-left-1 ' +
          className
        }
        style={{ minWidth, maxWidth }}
      >
        {children}
        {arrow && <RadixPopover.Arrow className="fill-paper stroke-line" width={12} height={6} />}
      </RadixPopover.Content>
    </RadixPopover.Portal>
  );
}
Popover.Content = Content;

interface PopoverHeaderProps {
  /** Mono uppercase eyebrow. */
  eyebrow?: ReactNode;
  /** Optional right-aligned hint (e.g. count). */
  hint?: ReactNode;
  className?: string;
}

function Header({ eyebrow, hint, className = '' }: PopoverHeaderProps) {
  return (
    <div
      className={
        'flex items-baseline justify-between border-b border-line px-3 pb-2 pt-2.5 font-mono text-[10.5px] uppercase tracking-[0.06em] text-ink-3 ' +
        className
      }
    >
      <span>{eyebrow}</span>
      {hint != null && <span>{hint}</span>}
    </div>
  );
}
Popover.Header = Header;

interface PopoverBodyProps {
  children: ReactNode;
  className?: string;
}

function Body({ children, className = '' }: PopoverBodyProps) {
  return <div className={'p-3 ' + className}>{children}</div>;
}
Popover.Body = Body;

Popover.Close = RadixPopover.Close;
